import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Five-card dashboard for the VAT Claim Lifecycle:
 *
 *   ┌─ Ready · Deferred · Claimed · Near Expiry · Forfeited ─┐
 *   └─ count + total VAT amount per card ────────────────────┘
 *
 * Receives the payload from ``GET /api/vat-claims/dashboard``.
 */
const Card = ({
  label,
  count,
  amount,
  tone = 'slate',
  icon,
  active = false,
  onClick,
  testid,
}) => {
  const tones = {
    indigo: 'border-indigo-300 bg-indigo-50/60 text-indigo-900 hover:bg-indigo-100',
    amber:  'border-amber-300 bg-amber-50/60 text-amber-900 hover:bg-amber-100',
    green:  'border-green-300 bg-green-50/60 text-green-900 hover:bg-green-100',
    red:    'border-red-300 bg-red-50/60 text-red-900 hover:bg-red-100',
    slate:  'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-start rounded-xl border p-4 transition-all ${tones} ${
        active ? 'ring-2 ring-offset-2 ring-indigo-500 shadow-md' : ''
      }`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-bold">{count ?? 0}</span>
        <span className="text-xs opacity-70">فاتورة</span>
      </div>
      <div className="text-xs mt-1 font-mono">
        {Number(amount ?? 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}{' '}
        SAR
      </div>
    </button>
  );
};

export const VATClaimDashboardCards = ({ data, loading, activeTab, onTabChange }) => {
  if (loading || !data) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin me-2" />
        تَحميل لَوحَة المُؤَشِّرات...
      </div>
    );
  }
  const c = data.cards || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="vat-claim-dashboard">
      <Card
        label={`جاهز لِلمُطالَبة · ${data.current_period}`}
        count={c.ready_to_claim_current?.count}
        amount={c.ready_to_claim_current?.amount}
        tone="indigo"
        active={activeTab === 'ready'}
        onClick={() => onTabChange('ready')}
        testid="vat-card-ready"
      />
      <Card
        label="مُؤَجَّلَة"
        count={c.deferred_total?.count}
        amount={c.deferred_total?.amount}
        tone="amber"
        active={activeTab === 'deferred'}
        onClick={() => onTabChange('deferred')}
        testid="vat-card-deferred"
      />
      <Card
        label={`تَمَّت المُطالَبة · ${data.current_period}`}
        count={c.claimed_current?.count}
        amount={c.claimed_current?.amount}
        tone="green"
        active={activeTab === 'claimed'}
        onClick={() => onTabChange('claimed')}
        testid="vat-card-claimed"
      />
      <Card
        label="قَريبَة من السُّقوط (6 أَشهُر)"
        count={c.near_expiry?.count}
        amount={c.near_expiry?.amount}
        tone="red"
        active={activeTab === 'near_expiry'}
        onClick={() => onTabChange('near_expiry')}
        testid="vat-card-near-expiry"
      />
      <Card
        label="سَقَطَت بِالتَّقادُم (YTD)"
        count={c.forfeited_ytd?.count}
        amount={c.forfeited_ytd?.amount}
        tone="slate"
        active={activeTab === 'forfeited'}
        onClick={() => onTabChange('forfeited')}
        testid="vat-card-forfeited"
      />
    </div>
  );
};

export default VATClaimDashboardCards;
